import { LabelConfig, LabelConfigVariant, Ref } from '@repo/sdk/models/components';

import { getRefValue } from '@/features/equipment-browser/utils';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';

import { ANIMATIC_PRIMARY_LABEL, ANIMATIC_SECONDARY_LABEL } from './animatics.constants';
import { AnimaticLabel, AnimaticLabelMap, LabelConfigMap } from './animatics.types';

/**
 * Gets a label from a map of labels by key
 * If the key is not found, it will return the first entry if the key starts with 'primary' and the second entry if the key starts with 'secondary'
 * If the key does not start with 'primary' or 'secondary', it will return the default label
 */
export const getLabel = (labels: AnimaticLabelMap, key: string): AnimaticLabel => {
  const defaultLabel = {
    text: '',
    variant: LabelConfigVariant.Primary,
  };
  if (key in labels) {
    return labels[key];
  }

  if (key.startsWith(ANIMATIC_PRIMARY_LABEL)) {
    const entries = Object.values(labels);
    return entries.length > 0 ? entries[0] : defaultLabel;
  }

  if (key.startsWith(ANIMATIC_SECONDARY_LABEL)) {
    const entries = Object.values(labels);
    return entries.length > 1 ? entries[1] : defaultLabel;
  }

  return defaultLabel;
};

// Gets the banner label from the label configs
export const getBannerLabel = (labelConfigs: LabelConfigMap): AnimaticLabel | undefined => {
  for (const labelConfig of Object.values(labelConfigs)) {
    if (labelConfig.isBanner) {
      return {
        text: labelConfig.value ?? '',
        variant: labelConfig.variant ?? LabelConfigVariant.Primary,
        subText: parseBannerSubText(labelConfig),
      };
    }
  }
  return undefined;
};

/**
 * A banner's sub-label (the fault reason on a faulted animatic): its ref's formatted
 * value, with underscores shown as spaces. Undefined when the banner has no ref.
 */
export const parseBannerSubText = (labelConfig: LabelConfig): string | undefined =>
  labelConfig.ref
    ? parseLabelRef(labelConfig.ref, labelConfig.displayUnits).replaceAll('_', ' ')
    : undefined;

export const parseLabelRef = (ref: Ref, displayUnits?: string) => {
  const value = getRefValue(ref);
  if (value !== undefined) {
    let sourceUnits = ref.units;
    if (!sourceUnits) {
      sourceUnits = typeof value === 'boolean' ? 'boolean' : displayUnits;
    }
    const formattedLabels = formatUnits({ value, units: sourceUnits }, displayUnits);
    return formattedLabels.valueUnitsLabel;
  }
  return '';
};

export const getAnimaticLabels = (labelConfigs: LabelConfigMap): AnimaticLabelMap => {
  const animaticLabels: { [key: string]: AnimaticLabel } = {};
  Object.entries(labelConfigs).forEach(([key, labelConfig]) => {
    if (labelConfig.isBanner) {
      return;
    }
    animaticLabels[key] = {
      text: labelConfig.ref
        ? parseLabelRef(labelConfig.ref, labelConfig.displayUnits)
        : (labelConfig.value ?? ''),
      variant: labelConfig.variant ?? LabelConfigVariant.Primary,
    };
  });
  return animaticLabels;
};
